import React, { useState, useEffect, useRef } from 'react';
import { get } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const LOGO_URL = '/uploads/photos/bloans-logo.jpg';
const COMPANY_NAME = 'Bloans Creations Ltd';
const COMPANY_ADDRESS = 'No 3, S. Ade-Ojo Estate, Sawmill, Old Ife Road, Ibadan, Oyo State';
const COMPANY_PHONE = '08028708436';
const COMPANY_EMAIL = 'support@bloanshandbags.com';
const COMPANY_WEBSITE = 'www.bloanshandbags.com';

const ROLE_LABELS = {
  super_admin: 'Managing Director', admin: 'Administrator',
  sales_manager: 'Sales Manager', content_sales: 'Content & Sales',
  inventory_officer: 'Inventory Officer', production_supervisor: 'Production Supervisor',
  production_assistant: 'Production Assistant', production_staff: 'Production Staff',
  tailor: 'Tailor', driver: 'Driver', auditor: 'Auditor'
};

const DEPT_LABELS = {
  MG: 'Management', AD: 'Admin & HR', SM: 'Sales & Marketing', PR: 'Production'
};

function getDept(staffId) {
  if (!staffId) return '';
  const parts = staffId.split('-');
  return parts[2] || '';
}

function Barcode({ value, width=2, height=50 }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    if (!canvasRef.current || !value) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    // Simple barcode simulation - Code 39 style
    const chars = value.split('');
    const barWidth = width;
    let x = 10;
    canvas.width = value.length * barWidth * 11 + 20;
    canvas.height = height + 20;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000';
    // Draw bars based on char codes
    value.split('').forEach(char => {
      const code = char.charCodeAt(0);
      const pattern = code.toString(2).padStart(8,'0');
      pattern.split('').forEach((bit, i) => {
        if (bit === '1') {
          ctx.fillRect(x + i*barWidth, 5, barWidth-0.5, height);
        }
      });
      x += 9 * barWidth;
    });
    // Add text below
    ctx.fillStyle = '#000';
    ctx.font = '8px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(value, canvas.width/2, height + 16);
  }, [value, width, height]);
  return <canvas ref={canvasRef} style={{maxWidth:'100%'}} />;
}

function QRPlaceholder({ staffId, size=80 }) {
  const [src, setSrc] = React.useState(null);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    if (!staffId) return;
    const token = localStorage.getItem('bb_token');
    fetch('/api/users/'+staffId+'/qrcode', {
      headers: { Authorization: 'Bearer '+token }
    })
    .then(res => {
      if (!res.ok) throw new Error('Failed');
      return res.blob();
    })
    .then(blob => setSrc(URL.createObjectURL(blob)))
    .catch(() => setError(true));
  }, [staffId]);

  if (error) return (
    <div style={{width:size,height:size,background:'#fff',display:'flex',
      alignItems:'center',justifyContent:'center',borderRadius:4,
      fontSize:7,color:'#8A8A8A',textAlign:'center',padding:2}}>
      QR Code
    </div>
  );
  if (!src) return (
    <div style={{width:size,height:size,background:'#eee',borderRadius:4}} />
  );
  return <img src={src} alt="QR Code" style={{width:size,height:size}} />;
}

function IDCardFront({ staff }) {
  const dept = getDept(staff.staff_id);
  const deptLabel = DEPT_LABELS[dept] || 'Staff';
  const roleLabel = ROLE_LABELS[staff.role] || staff.role;
  // profileUrl used for QR via local endpoint

  return (
    <div style={{
      width:240, height:380, borderRadius:16, overflow:'hidden',
      background:'#F2EFE7',
      position:'relative', display:'flex', flexDirection:'column',
      boxShadow:'0 8px 32px rgba(0,0,0,.25)', fontFamily:'system-ui,sans-serif'
    }}>
      {/* Top bar */}
      <div style={{background:'#556B2F',padding:'12px 16px',
        borderBottom:'1px solid #A8893D'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <img src={LOGO_URL} alt="Bloans Logo"
            style={{width:32,height:32,borderRadius:4,objectFit:'contain',background:'#fff',padding:2,border:'1px solid #DDD4C0'}} />
          <div>
            <div style={{fontSize:9,fontWeight:700,color:'#A8893D',letterSpacing:'.05em',lineHeight:1}}>
              BLOANS CREATIONS LTD
            </div>
            <div style={{fontSize:7,color:'rgba(242,239,231,.7)',letterSpacing:'.08em',marginTop:1}}>
              STAFF IDENTIFICATION
            </div>
          </div>
        </div>
      </div>

      {/* Photo area */}
      <div style={{display:'flex',justifyContent:'center',padding:'16px 0 8px'}}>
        <div style={{width:80,height:80,borderRadius:'50%',
          border:'3px solid #A8893D',overflow:'hidden',
          background:'#DDD4C0',display:'flex',
          alignItems:'center',justifyContent:'center'}}>
          {staff.photo_url ? (
            <img src={staff.photo_url} alt={staff.name}
              style={{width:'100%',height:'100%',objectFit:'cover'}} />
          ) : (
            <span style={{fontSize:32,fontWeight:700,color:'#A8893D'}}>
              {staff.name?.charAt(0)?.toUpperCase()}
            </span>
          )}
        </div>
      </div>

      {/* Name and role */}
      <div style={{textAlign:'center',padding:'0 12px 8px'}}>
        <div style={{fontFamily:'Cormorant Garamond,serif',fontSize:16,fontWeight:700,
          color:'#3B2A21',lineHeight:1.2,marginBottom:4}}>
          {staff.name}
        </div>
        <div style={{fontSize:10,fontWeight:600,color:'#A8893D',letterSpacing:'.08em',
          textTransform:'uppercase',marginBottom:2}}>
          {roleLabel}
        </div>
        <div style={{fontSize:9,color:'#8A8A8A',letterSpacing:'.06em'}}>
          {deptLabel} Department
        </div>
      </div>

      {/* Divider */}
      <div style={{margin:'0 16px',height:1,background:'#DDD4C0'}}></div>

      {/* Staff ID */}
      <div style={{padding:'8px 16px',textAlign:'center'}}>
        <div style={{fontSize:7,color:'#8A8A8A',letterSpacing:'.1em',
          textTransform:'uppercase',marginBottom:3}}>Staff ID</div>
        <div style={{fontSize:13,fontWeight:700,color:'#A8893D',
          letterSpacing:'.05em',fontFamily:'monospace'}}>
          {staff.staff_id}
        </div>
        {staff.date_joined && (
          <div style={{fontSize:8,color:'rgba(242,239,231,.4)',marginTop:3}}>
            Joined: {new Date(staff.date_joined).toLocaleDateString('en-NG',{day:'numeric',month:'long',year:'numeric'})}
          </div>
        )}
      </div>

      {/* Barcode */}
      <div style={{padding:'4px 8px',display:'flex',justifyContent:'center',
        background:'#556B2F'}}>
        <div style={{background:'#fff',borderRadius:4,padding:'4px 8px',display:'inline-block'}}>
          <Barcode value={staff.staff_id} width={1.2} height={30} />
        </div>
      </div>

      {/* QR Code */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',
        padding:'6px 12px 10px'}}>
        <div style={{background:'#fff',borderRadius:6,padding:3}}>
          <QRPlaceholder staffId={staff.id} size={50} />
        </div>
        <div style={{textAlign:'right'}}>
          <div style={{fontSize:7,color:'rgba(242,239,231,.3)',lineHeight:1.4}}>
            Scan to verify<br/>staff identity
          </div>
        </div>
      </div>
    </div>
  );
}

function IDCardBack({ staff }) {
  return (
    <div style={{
      width:240, height:380, borderRadius:16, overflow:'hidden',
      background:'#F2EFE7', position:'relative',
      display:'flex', flexDirection:'column',
      boxShadow:'0 8px 32px rgba(0,0,0,.25)', fontFamily:'system-ui,sans-serif'
    }}>
      {/* Top */}
      <div style={{background:'#3B2A21',padding:'10px 14px',
        display:'flex',alignItems:'center',gap:8}}>
        <img src={LOGO_URL} alt="Bloans" style={{width:24,height:24,
          borderRadius:3,objectFit:'contain',background:'#fff',padding:2}} />
        <div style={{fontSize:9,fontWeight:700,color:'#A8893D',letterSpacing:'.05em'}}>
          BLOANS CREATIONS LTD
        </div>
      </div>

      {/* Watermark */}
      <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%) rotate(-30deg)',
        fontSize:40,fontWeight:900,color:'rgba(85,107,47,.06)',whiteSpace:'nowrap',
        pointerEvents:'none',zIndex:0}}>
        BLOANS
      </div>

      <div style={{padding:'10px 14px',flex:1,position:'relative',zIndex:1}}>
        {/* Company info */}
        <div style={{marginBottom:10}}>
          <div style={{fontSize:8,fontWeight:700,color:'#556B2F',textTransform:'uppercase',
            letterSpacing:'.08em',marginBottom:4}}>Company Contact</div>
          <div style={{fontSize:8,color:'#3B2A21',lineHeight:1.6}}>
            <div>📍 {COMPANY_ADDRESS}</div>
            <div>📞 {COMPANY_PHONE}</div>
            <div>✉ {COMPANY_EMAIL}</div>
            <div>🌐 {COMPANY_WEBSITE}</div>
          </div>
        </div>

        {/* Emergency contact */}
        <div style={{marginBottom:10,padding:'8px 10px',background:'rgba(85,107,47,.08)',
          borderRadius:6,border:'1px solid rgba(85,107,47,.15)'}}>
          <div style={{fontSize:8,fontWeight:700,color:'#556B2F',textTransform:'uppercase',
            letterSpacing:'.08em',marginBottom:4}}>Emergency Contact</div>
          {staff.emergency_contact_name ? (
            <div style={{fontSize:8,color:'#3B2A21',lineHeight:1.6}}>
              <div>{staff.emergency_contact_name}</div>
              <div>{staff.emergency_contact_phone}</div>
            </div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'1fr',gap:3}}>
              <div style={{height:14,background:'rgba(0,0,0,.06)',borderRadius:3}}></div>
              <div style={{height:14,background:'rgba(0,0,0,.06)',borderRadius:3,width:'60%'}}></div>
            </div>
          )}
          {staff.blood_group && (
            <div style={{marginTop:4,fontSize:9,fontWeight:700,color:'#8F2E24'}}>
              Blood Group: {staff.blood_group}
            </div>
          )}
        </div>

        {/* If found */}
        <div style={{marginBottom:8,padding:'6px 10px',background:'#fff',
          borderRadius:6,border:'1px solid #DDD4C0'}}>
          <div style={{fontSize:7,fontWeight:700,color:'#8F2E24',textTransform:'uppercase',
            letterSpacing:'.08em',marginBottom:2}}>If Found, Please Return To:</div>
          <div style={{fontSize:7,color:'#3B2A21',lineHeight:1.5}}>
            {COMPANY_NAME}<br/>
            {COMPANY_ADDRESS}<br/>
            {COMPANY_PHONE}
          </div>
        </div>

        {/* Confidentiality */}
        <div style={{fontSize:6.5,color:'#8A8A8A',lineHeight:1.4,textAlign:'justify'}}>
          This card is the property of Bloans Creations Ltd. It must be worn visibly at all
          times on company premises. Any misuse, loss or damage must be reported immediately
          to HR. Unauthorized use is a disciplinary offence.
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{background:'#556B2F',padding:'6px 14px',
        display:'flex',justifyContent:'space-between',alignItems:'center'}}>
        <div style={{fontSize:7,color:'rgba(242,239,231,.7)',fontFamily:'monospace'}}>
          {staff.staff_id}
        </div>
        <div style={{fontSize:6,color:'rgba(242,239,231,.5)'}}>
          Valid: {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}

export default function StaffIDCards() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('front');
  const printRef = useRef(null);

  const isAdmin = ['super_admin','admin'].includes(user?.role);

  useEffect(() => {
    get('/users').then(d => {
      setStaff((d.data||[]).filter(u => u.staff_id));
      setLoading(false);
    });
  }, []);

  const handlePrint = () => {
    const printContent = printRef.current?.innerHTML;
    if (!printContent) return;
    const win = window.open('','_blank');
    win.document.write(`
      <html><head><title>ID Card - ${selected?.name}</title>
      <style>
        body { margin:0; padding:20px; background:#fff; font-family:system-ui,sans-serif; }
        @media print { body { padding:0; } }
        canvas { display:block; }
        img { display:block; }
      </style>
      </head><body>${printContent}</body></html>
    `);
    win.document.close();
    setTimeout(() => { win.print(); }, 500);
  };

  if (!isAdmin) return (
    <div className="empty-state">
      <h3>Access Restricted</h3>
      <p>Only admin can generate staff ID cards.</p>
    </div>
  );

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Staff ID Cards</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>
            Generate and print staff identification cards
          </p>
        </div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'300px 1fr',gap:20}}>
        {/* Staff list */}
        <div style={{background:'#fff',border:'1px solid var(--border-light)',
          borderRadius:10,overflow:'hidden'}}>
          <div style={{padding:'12px 16px',background:'#F2EFE7',
            fontSize:10,fontWeight:600,color:'#556B2F',textTransform:'uppercase',
            letterSpacing:'.08em'}}>
            Select Staff Member
          </div>
          {loading ? <div className="spinner" style={{margin:20}}></div> : (
            staff.map(s => (
              <div key={s.id} onClick={()=>setSelected(s)}
                style={{display:'flex',alignItems:'center',gap:12,padding:'10px 16px',
                  cursor:'pointer',borderBottom:'1px solid #F2EFE7',
                  background:selected?.id===s.id?'#F2EFE7':'#fff',
                  transition:'background .15s'}}>
                <div style={{width:36,height:36,borderRadius:'50%',
                  background:'#556B2F',display:'flex',alignItems:'center',
                  justifyContent:'center',flexShrink:0}}>
                  {s.photo_url
                    ? <img src={s.photo_url} alt={s.name} style={{width:'100%',height:'100%',borderRadius:'50%',objectFit:'cover'}} />
                    : <span style={{fontSize:16,fontWeight:700,color:'#F2EFE7'}}>{s.name?.charAt(0)}</span>
                  }
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:13,fontWeight:500,color:'#3B2A21',
                    overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {s.name}
                  </div>
                  <div style={{fontSize:10,color:'#8A8A8A',fontFamily:'monospace'}}>
                    {s.staff_id}
                  </div>
                </div>
                {selected?.id===s.id && (
                  <span style={{fontSize:16,color:'#556B2F'}}>✓</span>
                )}
              </div>
            ))
          )}
        </div>

        {/* Card preview */}
        <div>
          {!selected ? (
            <div className="empty-state">
              <h3>Select a staff member</h3>
              <p>Choose a staff member from the list to preview their ID card</p>
            </div>
          ) : (
            <div>
              <div style={{display:'flex',gap:10,marginBottom:20,alignItems:'center'}}>
                <button className={'btn btn-sm '+(view==='front'?'btn-primary':'btn-outline')}
                  onClick={()=>setView('front')}>Front</button>
                <button className={'btn btn-sm '+(view==='back'?'btn-primary':'btn-outline')}
                  onClick={()=>setView('back')}>Back</button>
                <button className={'btn btn-sm '+(view==='both'?'btn-primary':'btn-outline')}
                  onClick={()=>setView('both')}>Both Sides</button>
                <div style={{flex:1}}></div>
                <button className="btn btn-primary" onClick={handlePrint}>
                  🖨 Print ID Card
                </button>
              </div>

              <div ref={printRef} style={{display:'flex',gap:20,flexWrap:'wrap'}}>
                {(view==='front'||view==='both') && <IDCardFront staff={selected} />}
                {(view==='back'||view==='both') && <IDCardBack staff={selected} />}
              </div>

              {/* Staff details for editing */}
              <div style={{marginTop:20,background:'#fff',border:'1px solid var(--border-light)',
                borderRadius:10,padding:16}}>
                <div style={{fontSize:10,fontWeight:600,color:'#556B2F',textTransform:'uppercase',
                  letterSpacing:'.08em',marginBottom:12}}>Card Information</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,fontSize:12}}>
                  <div><span style={{color:'#8A8A8A'}}>Name:</span> {selected.name}</div>
                  <div><span style={{color:'#8A8A8A'}}>Staff ID:</span> <strong style={{fontFamily:'monospace'}}>{selected.staff_id}</strong></div>
                  <div><span style={{color:'#8A8A8A'}}>Role:</span> {ROLE_LABELS[selected.role]||selected.role}</div>
                  <div><span style={{color:'#8A8A8A'}}>Date Joined:</span> {selected.date_joined ? new Date(selected.date_joined).toLocaleDateString('en-NG') : 'Not set'}</div>
                  <div><span style={{color:'#8A8A8A'}}>Blood Group:</span> {selected.blood_group||'Not set'}</div>
                  <div><span style={{color:'#8A8A8A'}}>Emergency Contact:</span> {selected.emergency_contact_name||'Not set'}</div>
                </div>
                <div style={{marginTop:12}}>
                  <div style={{fontSize:10,fontWeight:600,color:'#556B2F',textTransform:'uppercase',
                    letterSpacing:'.08em',marginBottom:8}}>Upload Staff Photo</div>
                  <div style={{display:'flex',gap:10,alignItems:'center'}}>
                    <div style={{width:50,height:50,borderRadius:'50%',border:'2px solid #556B2F',
                      overflow:'hidden',background:'#F2EFE7',display:'flex',alignItems:'center',justifyContent:'center'}}>
                      {selected.photo_url
                        ? <img src={selected.photo_url} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                        : <span style={{fontSize:20,fontWeight:700,color:'#556B2F'}}>{selected.name?.charAt(0)}</span>
                      }
                    </div>
                    <label style={{cursor:'pointer'}}>
                      <span className="btn btn-outline btn-sm" style={{fontSize:11}}>
                        📷 {selected.photo_url ? 'Change Photo' : 'Upload Photo'}
                      </span>
                      <input type="file" accept="image/*" style={{display:'none'}}
                        onChange={async (e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const fd = new FormData();
                          fd.append('photo', file);
                          const token = localStorage.getItem('bb_token');
                          const res = await fetch('/api/users/'+selected.id+'/photo', {
                            method:'POST', headers:{Authorization:'Bearer '+token}, body:fd
                          });
                          const data = await res.json();
                          if (res.ok) {
                            setSelected({...selected, photo_url: data.photo_url});
                            setStaff(staff.map(s => s.id===selected.id ? {...s,photo_url:data.photo_url} : s));
                          } else { alert(data.error); }
                        }} />
                    </label>
                    <div style={{fontSize:11,color:'#8A8A8A'}}>
                      JPG, PNG up to 5MB. Photo appears on ID card front.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
